import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUsers, FiCode, FiTrendingUp, FiEdit3, FiZap, FiTarget, FiArrowRight, FiCheck } = FiIcons;

function UseCasesPage() {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const stagger = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const useCases = [
    {
      title: 'Freelance Designers',
      description: 'Speed up your client projects by cloning inspiration and customizing it',
      icon: FiEdit3,
      color: 'purple',
      benefits: [
        'Clone competitor websites for client analysis',
        'Rapidly prototype ideas from inspiration',
        'Save time on repetitive design patterns',
        'Deliver projects 50% faster'
      ],
      testimonial: {
        quote: "ClonewebX has transformed my workflow. I can now deliver client projects in half the time while maintaining quality.",
        author: "Sarah Chen",
        role: "Freelance Designer"
      },
      stats: {
        metric: "Time Saved",
        value: "15+ hours",
        period: "per project"
      }
    },
    {
      title: 'Web Development Agencies',
      description: 'Scale your team\'s output with rapid prototyping and client presentations',
      icon: FiUsers,
      color: 'blue',
      benefits: [
        'Create client presentations quickly',
        'Standardize design patterns across projects',
        'Onboard junior developers faster',
        'Win more pitches with rapid mockups'
      ],
      testimonial: {
        quote: "Our agency now wins 3x more pitches because we can show clients working prototypes in days, not weeks.",
        author: "Marcus Rodriguez",
        role: "Agency Owner"
      },
      stats: {
        metric: "Pitch Win Rate",
        value: "3x higher",
        period: "success rate"
      }
    },
    {
      title: 'Startup Founders',
      description: 'Launch your MVP faster without a full development team',
      icon: FiZap,
      color: 'green',
      benefits: [
        'Build landing pages in minutes',
        'Test different designs quickly',
        'Save on development costs',
        'Focus on business, not coding'
      ],
      testimonial: {
        quote: "We launched our startup landing page in 2 hours instead of 2 weeks. ClonewebX gave us the speed we needed.",
        author: "Alex Kim",
        role: "Startup Founder"
      },
      stats: {
        metric: "Launch Speed",
        value: "10x faster",
        period: "time to market"
      }
    },
    {
      title: 'E-commerce Businesses',
      description: 'Quickly adapt successful store layouts and optimize conversions',
      icon: FiTrendingUp,
      color: 'orange',
      benefits: [
        'Clone high-converting product pages',
        'A/B test different layouts',
        'Adapt seasonal campaigns quickly',
        'Improve conversion rates'
      ],
      testimonial: {
        quote: "We cloned a competitor's checkout flow and increased our conversion rate by 23% in just one week.",
        author: "Jennifer Walsh",
        role: "E-commerce Manager"
      },
      stats: {
        metric: "Conversion Boost",
        value: "23%",
        period: "average increase"
      }
    },
    {
      title: 'Content Creators',
      description: 'Build professional portfolios and landing pages without technical skills',
      icon: FiTarget,
      color: 'pink',
      benefits: [
        'Create portfolio websites instantly',
        'Clone successful creator pages',
        'No coding knowledge required',
        'Focus on content, not technicalities'
      ],
      testimonial: {
        quote: "As a content creator, I needed a professional site fast. ClonewebX made it possible without learning to code.",
        author: "David Park",
        role: "Content Creator"
      },
      stats: {
        metric: "Setup Time",
        value: "30 minutes",
        period: "portfolio ready"
      }
    },
    {
      title: 'Enterprise Teams',
      description: 'Maintain design consistency across large organizations',
      icon: FiCode,
      color: 'indigo',
      benefits: [
        'Standardize company design patterns',
        'Onboard new team members quickly',
        'Maintain brand consistency',
        'Scale design systems efficiently'
      ],
      testimonial: {
        quote: "Our enterprise team uses ClonewebX to maintain consistent designs across 50+ internal tools.",
        author: "Rachel Thompson",
        role: "Design Systems Lead"
      },
      stats: {
        metric: "Team Efficiency",
        value: "40%",
        period: "productivity gain"
      }
    }
  ];

  const workflows = [
    {
      title: 'Design Research',
      description: 'Collect and analyze competitor designs',
      steps: [
        'Clone competitor websites',
        'Organize in design library',
        'Analyze successful patterns',
        'Apply learnings to projects'
      ]
    },
    {
      title: 'Rapid Prototyping',
      description: 'Build functional prototypes in minutes',
      steps: [
        'Find inspiration online',
        'Clone the base structure',
        'Customize with your content',
        'Share with stakeholders'
      ]
    },
    {
      title: 'Client Presentations',
      description: 'Impress clients with working demos',
      steps: [
        'Clone similar successful sites',
        'Adapt to client branding',
        'Present interactive prototype',
        'Win the project'
      ]
    }
  ];

  return (
    <div className="pt-16">
      {/* Header Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-800 mb-6">
              How teams use ClonewebX to <span className="text-purple-600">10x their productivity</span>
            </h1>
            <p className="text-xl text-slate-600 mb-8">
              From freelancers to enterprise teams, discover how ClonewebX transforms workflows across industries
            </p>
          </motion.div>
        </div>
      </section>

      {/* Use Cases Grid */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="grid lg:grid-cols-2 gap-12"
            initial="initial"
            animate="animate"
            variants={stagger}
          >
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden hover:shadow-xl transition-all duration-300"
              >
                {/* Header */}
                <div className={`p-8 ${
                  useCase.color === 'purple' ? 'bg-gradient-to-br from-purple-50 to-purple-100' :
                  useCase.color === 'blue' ? 'bg-gradient-to-br from-blue-50 to-blue-100' :
                  useCase.color === 'green' ? 'bg-gradient-to-br from-green-50 to-green-100' :
                  useCase.color === 'orange' ? 'bg-gradient-to-br from-orange-50 to-orange-100' :
                  useCase.color === 'pink' ? 'bg-gradient-to-br from-pink-50 to-pink-100' :
                  'bg-gradient-to-br from-indigo-50 to-indigo-100'
                }`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                      useCase.color === 'purple' ? 'bg-purple-500' :
                      useCase.color === 'blue' ? 'bg-blue-500' :
                      useCase.color === 'green' ? 'bg-green-500' :
                      useCase.color === 'orange' ? 'bg-orange-500' :
                      useCase.color === 'pink' ? 'bg-pink-500' :
                      'bg-indigo-500'
                    }`}>
                      <SafeIcon icon={useCase.icon} className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-slate-800 mb-2">{useCase.title}</h3>
                      <p className="text-slate-600">{useCase.description}</p>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8">
                  {/* Benefits */}
                  <div className="mb-8">
                    <h4 className="font-semibold text-slate-800 mb-4">Key Benefits:</h4>
                    <div className="space-y-3">
                      {useCase.benefits.map((benefit, benefitIndex) => (
                        <div key={benefitIndex} className="flex items-start gap-3">
                          <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-700">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="bg-slate-50 rounded-lg p-6 mb-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-slate-800 mb-1">{useCase.stats.value}</div>
                      <div className="text-slate-600 font-medium">{useCase.stats.metric}</div>
                      <div className="text-sm text-slate-500">{useCase.stats.period}</div>
                    </div>
                  </div>

                  {/* Testimonial */}
                  <div className="border-l-4 border-purple-200 pl-6">
                    <p className="text-slate-600 italic mb-4">"{useCase.testimonial.quote}"</p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-blue-400 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                        {useCase.testimonial.author.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <div className="font-semibold text-slate-800">{useCase.testimonial.author}</div>
                        <div className="text-slate-500 text-sm">{useCase.testimonial.role}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Common Workflows */}
      <section className="py-20 bg-slate-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-slate-800 mb-4">
              Common ClonewebX Workflows
            </h2>
            <p className="text-xl text-slate-600">
              Discover the most popular ways teams integrate ClonewebX into their process
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-3 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            {workflows.map((workflow, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white rounded-2xl p-8 shadow-lg"
              >
                <h3 className="text-xl font-bold text-slate-800 mb-4">{workflow.title}</h3>
                <p className="text-slate-600 mb-6">{workflow.description}</p>
                <div className="space-y-4">
                  {workflow.steps.map((step, stepIndex) => (
                    <div key={stepIndex} className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {stepIndex + 1}
                      </div>
                      <span className="text-slate-700">{step}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Success Metrics */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-slate-800 mb-4">
              Results our customers achieve
            </h2>
            <p className="text-xl text-slate-600">
              Real metrics from teams using ClonewebX in production
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-4 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            {[
              { metric: '75%', label: 'Faster Development', description: 'Average time saved on web projects' },
              { metric: '3x', label: 'More Pitches Won', description: 'Agencies using rapid prototyping' },
              { metric: '50+', label: 'Hours Saved', description: 'Per month for active users' },
              { metric: '23%', label: 'Higher Conversions', description: 'From optimized page layouts' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="text-center p-6 rounded-xl bg-gradient-to-br from-purple-50 to-blue-50"
              >
                <div className="text-4xl font-bold text-purple-600 mb-2">{stat.metric}</div>
                <div className="text-lg font-semibold text-slate-800 mb-2">{stat.label}</div>
                <div className="text-slate-600 text-sm">{stat.description}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Industry Examples */}
      <section className="py-20 bg-slate-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-slate-800 mb-4">
              Industry-Specific Applications
            </h2>
            <p className="text-xl text-slate-600">
              See how ClonewebX adapts to different industry needs
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            {[
              {
                industry: 'SaaS Companies',
                example: 'Clone competitor landing pages to analyze conversion strategies',
                icon: '💻'
              },
              {
                industry: 'E-learning Platforms',
                example: 'Replicate successful course layouts and student dashboards',
                icon: '📚'
              },
              {
                industry: 'Real Estate',
                example: 'Clone property listing designs and neighborhood pages',
                icon: '🏠'
              },
              {
                industry: 'Healthcare',
                example: 'Adapt patient portal designs and appointment systems',
                icon: '🏥'
              },
              {
                industry: 'Restaurants',
                example: 'Clone menu designs and online ordering systems',
                icon: '🍽️'
              },
              {
                industry: 'Fashion & Retail',
                example: 'Replicate product catalogs and checkout experiences',
                icon: '👕'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white rounded-lg p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
              >
                <div className="text-3xl mb-4">{item.icon}</div>
                <h3 className="text-lg font-semibold text-slate-800 mb-3">{item.industry}</h3>
                <p className="text-slate-600 text-sm">{item.example}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-slate-800 mb-4">
              Ready to transform your workflow?
            </h2>
            <p className="text-xl text-slate-600 mb-8">
              Join thousands of professionals who have revolutionized their design process with ClonewebX
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-purple-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-purple-700 transition-all transform hover:scale-105 flex items-center justify-center gap-2">
                Start Cloning for Free
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </button>
              <button className="border-2 border-slate-300 text-slate-700 px-8 py-4 rounded-lg font-semibold text-lg hover:border-slate-400 transition-all">
                See Live Demo
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default UseCasesPage;